import api from './client';

export interface HealthReminder {
  id: string;
  birdId: string;
  reminderType: 'HEALTH_CHECKUP' | 'VACCINATION' | 'MEDICATION' | 'WEIGHT_CHECK' | 'BLOOD_TEST' | 'FECAL_EXAM' | 'GENERAL_OBSERVATION' | 'CUSTOM';
  title: string;
  description?: string;
  reminderDate: string;
  frequency: 'ONCE' | 'DAILY' | 'WEEKLY' | 'MONTHLY' | 'QUARTERLY' | 'YEARLY';
  priority: 'LOW' | 'MEDIUM' | 'HIGH' | 'URGENT';
  status: 'ACTIVE' | 'COMPLETED' | 'CANCELLED' | 'SNOOZED';
  notificationMethods: ('EMAIL' | 'SMS' | 'PUSH' | 'IN_APP')[];
  nextReminderDate?: string;
  lastSentAt?: string;
  sentCount: number;
  maxReminders?: number;
  createdBy?: string;
  completedAt?: string;
  completionNotes?: string;
  metadata?: any;
  bird?: any;
  createdAt: string;
  updatedAt: string;
}

export interface CreateHealthReminderDto {
  birdId: string;
  reminderType: 'HEALTH_CHECKUP' | 'VACCINATION' | 'MEDICATION' | 'WEIGHT_CHECK' | 'BLOOD_TEST' | 'FECAL_EXAM' | 'GENERAL_OBSERVATION' | 'CUSTOM';
  title: string;
  description?: string;
  reminderDate: string;
  frequency: 'ONCE' | 'DAILY' | 'WEEKLY' | 'MONTHLY' | 'QUARTERLY' | 'YEARLY';
  priority: 'LOW' | 'MEDIUM' | 'HIGH' | 'URGENT';
  notificationMethods: ('EMAIL' | 'SMS' | 'PUSH' | 'IN_APP')[];
  nextReminderDate?: string;
  maxReminders?: number;
  createdBy?: string;
  metadata?: any;
}

export interface UpdateHealthReminderDto {
  reminderType?: 'HEALTH_CHECKUP' | 'VACCINATION' | 'MEDICATION' | 'WEIGHT_CHECK' | 'BLOOD_TEST' | 'FECAL_EXAM' | 'GENERAL_OBSERVATION' | 'CUSTOM';
  title?: string;
  description?: string;
  reminderDate?: string;
  frequency?: 'ONCE' | 'DAILY' | 'WEEKLY' | 'MONTHLY' | 'QUARTERLY' | 'YEARLY';
  priority?: 'LOW' | 'MEDIUM' | 'HIGH' | 'URGENT';
  status?: 'ACTIVE' | 'COMPLETED' | 'CANCELLED' | 'SNOOZED';
  notificationMethods?: ('EMAIL' | 'SMS' | 'PUSH' | 'IN_APP')[];
  nextReminderDate?: string;
  lastSentAt?: string;
  sentCount?: number;
  maxReminders?: number;
  completedAt?: string;
  completionNotes?: string;
  metadata?: any;
}

export const healthRemindersApi = {
  // Create a new health reminder
  async create(createDto: CreateHealthReminderDto): Promise<HealthReminder> {
    const response = await api.post('health-reminders', createDto);
    return response.data as HealthReminder;
  },

  // Get all health reminders
  async getAll(): Promise<HealthReminder[]> {
    const response = await api.get('health-reminders');
    return response.data as HealthReminder[];
  },

  // Get health reminder by ID
  async getById(id: string): Promise<HealthReminder> {
    const response = await api.get(`health-reminders/${id}`);
    return response.data as HealthReminder;
  },

  // Get health reminders for a specific bird
  async getByBirdId(birdId: string): Promise<HealthReminder[]> {
    const response = await api.get(`health-reminders/bird/${birdId}`);
    return response.data as HealthReminder[];
  },

  // Get health reminders by status
  async getByStatus(status: string): Promise<HealthReminder[]> {
    const response = await api.get(`health-reminders/status/${status}`);
    return response.data as HealthReminder[];
  },

  // Get active health reminders
  async getActive(): Promise<HealthReminder[]> {
    const response = await api.get('health-reminders/active');
    return response.data as HealthReminder[];
  },

  // Get due health reminders
  async getDue(): Promise<HealthReminder[]> {
    const response = await api.get('health-reminders/due');
    return response.data as HealthReminder[];
  },

  // Get upcoming health reminders
  async getUpcoming(days?: number): Promise<HealthReminder[]> {
    const params = days ? `?days=${days}` : '';
    const response = await api.get(`health-reminders/upcoming${params}`);
    return response.data as HealthReminder[];
  },

  // Get health reminders by date range
  async getByDateRange(startDate: string, endDate: string): Promise<HealthReminder[]> {
    const response = await api.get(`health-reminders/date-range?startDate=${startDate}&endDate=${endDate}`);
    return response.data as HealthReminder[];
  },

  // Update health reminder
  async update(id: string, updateDto: UpdateHealthReminderDto): Promise<HealthReminder> {
    const response = await api.patch(`health-reminders/${id}`, updateDto);
    return response.data as HealthReminder;
  },

  // Mark reminder as completed
  async markAsCompleted(id: string, completionNotes?: string): Promise<HealthReminder> {
    const response = await api.patch(`health-reminders/${id}/complete`, {
      completionNotes,
    });
    return response.data as HealthReminder;
  },

  // Snooze reminder
  async snooze(id: string, snoozeUntil: string): Promise<HealthReminder> {
    const response = await api.patch(`health-reminders/${id}/snooze`, {
      snoozeUntil,
    });
    return response.data as HealthReminder;
  },

  // Cancel reminder
  async cancel(id: string, reason?: string): Promise<HealthReminder> {
    const response = await api.patch(`health-reminders/${id}/cancel`, {
      reason,
    });
    return response.data as HealthReminder;
  },

  // Process recurring reminder
  async processRecurring(id: string): Promise<HealthReminder> {
    const response = await api.patch(`health-reminders/${id}/process`);
    return response.data as HealthReminder;
  },

  // Delete health reminder
  async delete(id: string): Promise<void> {
    await api.delete(`health-reminders/${id}`);
  },

  // Get statistics
  async getStatistics(): Promise<any> {
    const response = await api.get('health-reminders/statistics');
    return response.data as any;
  },

  // Get reminders by type
  async getByType(): Promise<Record<string, number>> {
    const response = await api.get('health-reminders/statistics/by-type');
    return response.data as Record<string, number>;
  },

  // Get reminders by priority
  async getByPriority(): Promise<Record<string, number>> {
    const response = await api.get('health-reminders/statistics/by-priority');
    return response.data as Record<string, number>;
  },

  // Get reminders by frequency
  async getByFrequency(): Promise<Record<string, number>> {
    const response = await api.get('health-reminders/statistics/by-frequency');
    return response.data as Record<string, number>;
  },
}; 